import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { api, type OwnerPostSummary, type PostKind } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { announcePostClosed } from "./PostLifecycleNotice";
import { SaveButton } from "./SaveButton";
import { useCancelRefresh } from "../lib/useRefreshOwner";

type OwnerPostMenuProps = {
  kind: PostKind;
  post: OwnerPostSummary;
  className?: string;
  onChanged?: () => void | Promise<void>;
  serviceActions?: { archive: () => void; restore: () => void; busy: boolean };
  extraActions?: { label: string; run: () => void; disabled?: boolean }[];
};

export function OwnerPostMenu({ kind, post, className = "absolute right-3 top-3 z-20", onChanged, serviceActions, extraActions = [] }: OwnerPostMenuProps) {
  const cancelRefresh = useCancelRefresh();
  const { user } = useAuth();
  const { t } = useI18n();
  const menuId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = post.title || post.name || "Post";
  // [posting.close](../../../docs/product/marketplace.md#posting-close): unknown ownership and company membership cannot grant post actions.
  const isOwner = !!user && post.ownerUserId === user.id;
  const closed = post.ownerClosed || (kind === "service" ? !!post.status && post.status !== "open" : post.hidden || (kind === "item" && !!post.status && post.status !== "active"));

  useEffect(() => {
    if (!position) return;
    if (!panel.current?.contains(document.activeElement)) panel.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target) && !trigger.current?.contains(event.target)) setPosition(null);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setPosition(null); trigger.current?.focus(); }
    };
    const reposition = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      if (rect.bottom < 0 || rect.top > window.innerHeight) { setPosition(null); return; }
      const top = Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - (panel.current?.offsetHeight || 160) - 8));
      const left = Math.max(8, Math.min(rect.right - 192, window.innerWidth - 200));
      setPosition((current) => current && (current.top !== top || current.left !== left) ? { top, left } : current);
    };
    reposition();
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", key);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [position]);

  useEffect(() => {
    if (!confirmClose) return;
    const previous = document.activeElement;
    dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) setConfirmClose(false);
      if (event.key === "Tab") {
        const buttons = [...(dialog.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") || [])];
        if (!buttons.length) return;
        event.preventDefault();
        const current = buttons.findIndex(button => button === document.activeElement);
        buttons[(current + (event.shiftKey ? buttons.length - 1 : 1)) % buttons.length]?.focus();
      }
    };
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("keydown", key); if (previous instanceof HTMLElement) previous.focus(); };
  }, [confirmClose, busy]);

  const canReopen = closed && !post.moderationHidden && post.status !== "removed" && post.status !== "awarded";
  const hasServiceAction = kind === "service" && serviceActions && (post.status === "closed" || post.status === "archived");
  if (!isOwner || (closed && !canReopen && !hasServiceAction && extraActions.length === 0)) return null;
  const changed = async () => {
    if (onChanged) await onChanged();
    else window.location.reload();
  };
  async function close() {
    cancelRefresh();
    setBusy(true); setError(null);
    try {
      if (kind === "item") {
        const archived = await api.archiveItem(post.id);
        if (!archived) throw new Error(t("post.owner.closeError"));
      }
      else await api.closePost(kind, post.id);
      setPosition(null); setConfirmClose(false);
      announcePostClosed(kind, post.id, title, post.status !== "awarded" && !post.moderationHidden);
      await changed();
    } catch (cause) { setError(cause instanceof Error ? cause.message : t("post.owner.closeError")); }
    finally { setBusy(false); }
  }
  async function reopen() {
    cancelRefresh();
    setBusy(true); setError(null);
    try { await api.reopenPost(kind, post.id); setPosition(null); await changed(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : t("post.owner.reopenError")); }
    finally { setBusy(false); }
  }
  return (
    <span className={className} data-refresh-mutation={busy || undefined} onClick={(event) => event.stopPropagation()}>
      <button ref={trigger} type="button" disabled={busy || serviceActions?.busy} aria-label={`${t("post.owner.actions")}: ${title}`}
        aria-haspopup="menu" aria-expanded={!!position} aria-controls={position ? menuId : undefined}
        onClick={(event) => {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          setPosition(position ? null : { top: Math.min(rect.bottom + 4, window.innerHeight - 160), left: Math.max(8, Math.min(rect.right - 192, window.innerWidth - 200)) });
        }}
        className="grid h-9 w-9 place-items-center rounded-full border border-line bg-white text-navy shadow-sm hover:bg-navy-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-red disabled:opacity-50">
        <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
      </button>
      {position && createPortal(
        <div ref={panel} id={menuId} role="menu" aria-label={`${t("post.owner.actions")}: ${title}`} style={position}
          className="fixed z-[70] max-h-[calc(100dvh_-_1rem)] w-48 overflow-y-auto rounded-xl border border-line bg-white p-1 shadow-lift" onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
            const current = buttons.findIndex((button) => button === document.activeElement);
            const index = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (current + (event.key === "ArrowDown" ? 1 : buttons.length - 1)) % buttons.length;
            buttons[index]?.focus();
          }}>
          {!closed && <button role="menuitem" type="button" onClick={() => { setPosition(null); setConfirmClose(true); }} disabled={busy} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-brand-red hover:bg-navy-50">{t("post.owner.close")}</button>}
          {canReopen && <button role="menuitem" type="button" onClick={() => { void reopen(); }} disabled={busy} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-navy-50">{t("post.owner.reopen")}</button>}
          {kind === "service" && serviceActions && post.status === "closed" && <button role="menuitem" type="button" onClick={() => { setPosition(null); serviceActions.archive(); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-navy-50">{t("dashboard.service.archive")}</button>}
          {kind === "service" && serviceActions && post.status === "archived" && <button role="menuitem" type="button" onClick={() => { setPosition(null); serviceActions.restore(); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-navy-50">{t("dashboard.service.restore")}</button>}
          {extraActions.map((action) => <button key={action.label} role="menuitem" type="button" disabled={action.disabled} onClick={() => { setPosition(null); action.run(); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-navy-50 disabled:opacity-50">{action.label}</button>)}
        </div>, document.body)}
      {confirmClose && createPortal(<div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" onClick={(event) => event.stopPropagation()}>
        <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={menuId + "-close-title"} aria-describedby={menuId + "-close-body"} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lift">
          <h2 id={menuId + "-close-title"} className="text-lg font-bold text-navy">{t("post.owner.confirmTitle")}</h2>
          <p className="mt-2 font-semibold text-navy">{title}</p>
          <p id={menuId + "-close-body"} className="mt-2 text-sm text-mut">{t("post.owner.confirmBody")}</p>
          <div className="mt-5 flex justify-end gap-3"><button type="button" disabled={busy} onClick={() => setConfirmClose(false)} className="btn-secondary">{t("post.owner.cancel")}</button><button type="button" disabled={busy} onClick={() => { void close(); }} className="btn-primary">{t("post.owner.close")}</button></div>
        </div>
      </div>, document.body)}
      {error && createPortal(<div role="alert" className="fixed bottom-4 left-4 right-4 z-[80] rounded-xl border border-brand-red bg-white p-4 text-sm text-brand-red" onClick={(event) => event.stopPropagation()}>{error}<button type="button" className="ml-3 underline" onClick={() => setError(null)}>{t("common.dismiss", "Dismiss")}</button></div>, document.body)}
    </span>
  );
}

export function PostCardActions({ kind, post, className, imageOverlay = false, iconClassName }: { kind: PostKind; post: OwnerPostSummary; className?: string; imageOverlay?: boolean; iconClassName?: string }) {
  const { user } = useAuth();
  return user && post.ownerUserId === user.id
    ? <OwnerPostMenu kind={kind} post={post} className={className} />
    : <SaveButton kind={kind} refId={post.id} className={className} imageOverlay={imageOverlay} iconClassName={iconClassName} />;
}
